import React, { type Dispatch, type ReactNode, type SetStateAction, Suspense, lazy, useEffect, useState } from 'react'
import TreeRoute from '@fs/zion-tree-route'
import { useOverlay } from '@fs/zion-ui'
import { Redirect, useParams, Route, NotFound, Switch } from '@fs/zion-router'
import { pidRegexStr } from '@fs/zion-pid'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { type PID } from '@fs/zion-tree-types'
import CenteredLoadingSpinner from './components/CenteredLoadingSpinner'
import { usePedigreeData } from './services/pedigrees'
import useDataAdmin from './hooks/landscape/useDataAdmin'
import TreeProcessingOverlay from './components/TreeProcessingOverlay'

const v9PedigreeViews = ['first-ancestor', 'landscape', 'portrait', 'fanchart', 'descendancy']

export type PedigreeViewType = 'landscape' | 'portrait' | 'descendancy' | 'first-ancestor' | 'fanchart'

const PedigreeView = lazy(() => import('./pages/PedigreeView'))

type RedirectProps = {
  lastPid: PID | undefined
  lastView: PedigreeViewType | undefined
}

const RedirectViewAndPid = ({ lastPid, lastView }: RedirectProps): JSX.Element => {
  return <Redirect to={`/pedigree/${lastView}/${lastPid}`} />
}
const RedirectViewOnly = ({ lastPid }: Pick<RedirectProps, 'lastPid'>): JSX.Element => {
  const { view } = useParams()
  return <Redirect to={`/pedigree/${view}/${lastPid}`} />
}
const RedirectPidOnly = ({ lastView }: Pick<RedirectProps, 'lastView'>): JSX.Element => {
  const { pid } = useParams()
  return <Redirect to={`/pedigree/${lastView}/${pid}`} />
}

type TreeCheckProps = {
  children: ReactNode
  startingPid: PID | undefined
  setShowOverview: Dispatch<SetStateAction<boolean>>
}

const TreeCheck = ({ children, startingPid, setShowOverview }: TreeCheckProps): JSX.Element => {
  const { currentTreeId, currentGroup, treeDetails, openGroupOverlay } = useGroupsContext()
  const treeProcessingOverlay = useOverlay({ initiallyOpen: treeDetails?.hidden })
  const { userIsDataAdmin } = useDataAdmin()

  // If no startingPid came back tree-data it means the person hasn't been copied yet.
  useEffect(() => {
    if (
      openGroupOverlay &&
      typeof currentTreeId === 'string' &&
      currentTreeId !== 'PRIVATE' &&
      currentTreeId !== '' &&
      treeDetails &&
      !treeDetails.hidden &&
      !userIsDataAdmin
    ) {
      if (!startingPid) openGroupOverlay()
      else setShowOverview(true)
    }
  }, [currentTreeId, openGroupOverlay, startingPid, treeDetails, userIsDataAdmin, setShowOverview])

  if (treeDetails?.hidden) {
    return (
      <>
        {(treeProcessingOverlay.isOpen || treeProcessingOverlay.transitioning) && (
          <TreeProcessingOverlay overlay={treeProcessingOverlay} groupId={currentGroup?.id} />
        )}
      </>
    )
  }
  return <>{children}</>
}

type PedigreeRoutesProps = {
  setReadOnlyMode: Dispatch<SetStateAction<boolean>>
}

const PedigreeRoutes = ({ setReadOnlyMode }: PedigreeRoutesProps): JSX.Element => {
  const { currentTreeId, loading: groupsLoading, isReadOnlyTree, isResearcherGroup } = useGroupsContext()
  const [showOverview, setShowOverview] = useState(false)

  // get last viewed pid and view
  const { lastView, lastPid, updateLastView, updateLastPid, startingPid, loading } = usePedigreeData({
    treeId: currentTreeId,
    groupsLoading,
  })

  if (loading || groupsLoading) return <CenteredLoadingSpinner />
  return (
    <Switch>
      {/* Handle /pedigree */}
      <TreeRoute exact path="/pedigree">
        <TreeCheck startingPid={startingPid} setShowOverview={setShowOverview}>
          <RedirectViewAndPid lastPid={lastPid} lastView={lastView} />
        </TreeCheck>
      </TreeRoute>

      {/* Handle /pedigree/<view> */}
      <TreeRoute exact path={`/pedigree/:view(${v9PedigreeViews.join('|')})`}>
        <TreeCheck startingPid={startingPid} setShowOverview={setShowOverview}>
          <RedirectViewOnly lastPid={lastPid} />
        </TreeCheck>
      </TreeRoute>

      {/* Handle /pedigree/<pid> */}
      <TreeRoute exact path={`/pedigree/:pid(${pidRegexStr})`}>
        <TreeCheck startingPid={startingPid} setShowOverview={setShowOverview}>
          <RedirectPidOnly lastView={lastView} />
        </TreeCheck>
      </TreeRoute>

      {/* Once we have a full url (/pedigree/<view>/<pid>) render the page  */}
      <TreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        setReadOnlyMode={setReadOnlyMode}
        exact
        path={`/pedigree/:view(${v9PedigreeViews.join('|')})/:pid(${pidRegexStr})`}
      >
        <Suspense fallback={<CenteredLoadingSpinner />}>
          <PedigreeView
            isResearcherGroup={isResearcherGroup}
            showOverview={showOverview}
            isReadOnlyTree={isReadOnlyTree}
            currentTreeId={currentTreeId}
            startingPid={startingPid}
            updateLastView={updateLastView}
            updateLastPid={updateLastPid}
          />
        </Suspense>
      </TreeRoute>
      <Route component={NotFound} />
    </Switch>
  )
}

export default PedigreeRoutes
